import { CommonUIError } from '@afterpay/types'
import { isCallable } from '@afterpay/utils'
import { ReactNode } from 'react'
import { GeoArgs, GeoReturn } from 'use-places-autocomplete'

import { noop } from '../utils/function'
import { isUndefinedOrEmpty } from '../utils/string'
import { DEFAULT_ALLOWED_ADDRESS_TYPES } from './constants'
import { InputAddressData, InputAddressValidationRule, MandatoryFields, ProvidedAddress } from './types'

interface InputAddressAddressData {
  valid: boolean
  data: InputAddressData
}

export type AddressParser = (data: google.maps.GeocoderResult) => InputAddressAddressData
export interface CustomGeocodeResult {
  formData: InputAddressData
  formattedAddress: string
}

// Export to type utils
// Gives you a type where the keys are the keys of the generic type provided
// Obeys optionals. Values will be a string
type StringifyWithOptionalKeys<T> = {
  [K in keyof T]: undefined extends T[K] ? string | undefined : string
}

// Google's address component types
export interface CountryAddressComponents {
  unitNumber: string[]
  streetNumber: string[]
  route: string[]
  suburb: string[]
  state?: string[]
  postcode: string[]
  countryCode: string[]
  area2?: string[]
}

interface CountryValidationComponent {
  components: Array<keyof CountryAddressComponents>
  validationMatchType: 'some' | 'every'
}
interface RequiredAddressComponents {
  [countryCode: string]: CountryValidationComponent
}

export const REQUIRED_ADDRESS_COMPONENTS: RequiredAddressComponents = {
  AU: { components: ['unitNumber', 'streetNumber'], validationMatchType: 'some' },
} as const

export const CONFIG: {
  [x: string]: CountryAddressComponents
} = {
  US: {
    unitNumber: ['subpremise'],
    streetNumber: ['street_number'],
    route: ['route'],
    // PAYL-6936 Parts of Brooklyn and other New York addresses
    // use the sublocality_level_1 field for suburb instead of locality
    suburb: ['locality', 'sublocality_level_1'],
    state: ['administrative_area_level_1'],
    postcode: ['postal_code'],
    countryCode: ['country'],
  },
  AU: {
    unitNumber: ['subpremise'],
    streetNumber: ['street_number'],
    route: ['route'],
    suburb: ['locality'],
    state: ['administrative_area_level_1'],
    postcode: ['postal_code'],
    countryCode: ['country'],
  },
  NZ: {
    unitNumber: ['subpremise'],
    streetNumber: ['street_number'],
    route: ['route'],
    // PAYL-11958 sublocality_level_1 does not always available
    // use sublocality_level_1 if available, otherwise fallback to locality
    suburb: ['locality', 'sublocality_level_1'],
    state: ['locality'],
    postcode: ['postal_code'],
    countryCode: ['country'],
  },
  CA: {
    unitNumber: ['subpremise'],
    streetNumber: ['street_number'],
    route: ['route'],
    suburb: ['locality', 'sublocality_level_1'],
    state: ['administrative_area_level_1'],
    postcode: ['postal_code'],
    countryCode: ['country'],
  },
  GB: {
    unitNumber: ['subpremise'],
    streetNumber: ['street_number'],
    route: ['route'],
    suburb: ['postal_town'],
    state: ['administrative_area_level_2'],
    // For some GB addresses Google supplies only the outward code typed as
    // `postal_code_prefix` (e.g. "M24"). Capture it as a fallback so the postcode isn't
    // dropped to empty; `postal_code` is listed last so the full code wins when present.
    postcode: ['postal_code_prefix', 'postal_code'],
    area2: ['locality', 'neighbourhood'],
    countryCode: ['country'],
  },
  IT: {
    unitNumber: ['subpremise'],
    streetNumber: ['street_number'],
    route: ['route'],
    suburb: ['locality'],
    state: ['administrative_area_level_2'],
    postcode: ['postal_code'],
    countryCode: ['country'],
  },
  ES: {
    unitNumber: ['subpremise'],
    streetNumber: ['street_number'],
    route: ['route'],
    suburb: ['locality'],
    state: ['administrative_area_level_2'],
    postcode: ['postal_code'],
    countryCode: ['country'],
  },
  FR: {
    unitNumber: ['subpremise'],
    streetNumber: ['street_number'],
    route: ['route'],
    suburb: ['locality'],
    postcode: ['postal_code'],
    countryCode: ['country'],
  },
  IM: {
    // Isle of Man
    unitNumber: ['subpremise'],
    streetNumber: ['street_number'],
    route: ['route'],
    suburb: ['postal_town'],
    state: ['administrative_area_level_2'],
    postcode: ['postal_code'],
    area2: ['locality', 'neighbourhood'],
    countryCode: ['country'],
  },
  JE: {
    // Jersey
    unitNumber: ['subpremise'],
    streetNumber: ['street_number'],
    route: ['route'],
    suburb: ['postal_town'],
    state: ['administrative_area_level_2'],
    postcode: ['postal_code'],
    area2: ['locality', 'neighbourhood'],
    countryCode: ['country'],
  },
  GG: {
    // Guernsey
    unitNumber: ['subpremise'],
    streetNumber: ['street_number'],
    route: ['route'],
    suburb: ['postal_town'],
    state: ['administrative_area_level_2'],
    postcode: ['postal_code'],
    area2: ['locality', 'neighbourhood'],
    countryCode: ['country'],
  },
} as const

export const POSTCODE_FORMAT: {
  [x: string]: any
} = {
  US: '00000[-][0000]',
  AU: '0000',
  NZ: '0000',
  CA: 'a0a 0a0',
  GB: 'a*******',
  IM: 'IM0[0] [0aa]',
  JE: 'JE0[0] [0aa]',
  GG: 'GY0[0] [0aa]',
  IT: '00000',
  ES: '00000',
  FR: '00000',
} as const

interface ParseAddressConfig {
  allowedAddressType?: string[]
  country: string
  useLocalityLongName?: boolean
  validateRule?: InputAddressValidationRule
  isRegions?: boolean
  optionalFields?: string[]
}

const containsPoBox = (value: string): boolean => {
  const poBoxPattern = /\b(([G]?)(?:P(?:ost(?:al)?)?[,.\-\s]*(?:(?:O(?:ffice)?[,.\-\s]*)?B(?:ox|in|\b|\d?)|o(?:ffice|\b)(?:[-\s]*\d)|code)))/i
  return poBoxPattern.test(value)
}

const containsParcelLocker = (value: string): boolean => {
  const parcelLockerPattern = /\bParcel Locker/i
  return parcelLockerPattern.test(value)
}

const containsFrenchPoBox = (value: string): boolean => {
  const frenchPoBoxPattern = /\b(boîtes[,.\-\s]*(postales|boîtes))|(casier[,.\-\s]*à[,.\-\s]*colis)|(postales[,.\-\s]*boîtes)/i
  return frenchPoBoxPattern.test(value)
}

export const hasNoPOBox = (address: string, countryCode: string): boolean => {
  let hasNoPOBox = !containsPoBox(address) && !containsParcelLocker(address)

  if (countryCode === 'CA') {
    hasNoPOBox = hasNoPOBox && !containsFrenchPoBox(address)
  }

  return hasNoPOBox
}

// Used for manual address validation
// Caveat: this is intended only for address1 values.
export const hasUnitOrStreetNumber = (address: string): boolean => {
  if (isUndefinedOrEmpty(address)) {
    return false
  }

  const unitOrStreetNumberPattern = /^(?!.*[^\w\s\/,\-().#])(?=.*\b(\d+[A-Za-z]?\/\d+|(Lot\s*\d+[A-Za-z]?)|\d+[A-Za-z]?)\b)(?=.*[A-Za-z]{2,}).*$/i

  return unitOrStreetNumberPattern.test(address)
}

export const addressHasNoPO = (parsedData: InputAddressData): boolean => {
  return Object.keys(parsedData).every((key) => hasNoPOBox(parsedData[key as keyof InputAddressData], parsedData.countryCode))
}

export const doGeocode = async (
  address: ProvidedAddress,
  country: string,
  addressParser: AddressParser,
  onGeocode = noop
): Promise<CustomGeocodeResult | null> => {
  const { address1, address2, suburb, postcode, state } = address
  const providedAddress = `${address1 ?? ''} ${address2 ?? ''} ${suburb ?? ''} ${state ?? ''} ${postcode ?? ''}`

  // Geocode api will still return a result with component restrictions and empty address

  const componentRestrictions: google.maps.GeocoderComponentRestrictions | undefined = isUndefinedOrEmpty(providedAddress)
    ? undefined
    : {
        country,
      }

  onGeocode()
  return await geocodeByAddress(providedAddress, addressParser, componentRestrictions)
}

export const geocodeByAddress = async (
  address: string,
  addressParser: AddressParser,
  componentRestrictions?: google.maps.GeocoderComponentRestrictions
): Promise<CustomGeocodeResult | null> => {
  if (typeof window === 'undefined') {
    return null
  }

  const geocoder = new window.google.maps.Geocoder()
  const OK = window.google.maps.GeocoderStatus.OK

  return await new Promise((resolve, reject) => {
    geocoder.geocode({ address, componentRestrictions }, (results, status) => {
      if (results === null) {
        reject(new Error('no results'))
        return
      }

      if (status !== OK) {
        reject(status)
        return
      }

      const [firstResult] = results
      const { valid, data } = addressParser(firstResult)

      if (!valid) {
        reject(new Error('Did not pass address parser validation'))
        return
      }

      resolve({
        formData: data,
        formattedAddress: firstResult.formatted_address,
      })
    })
  })
}

export const doGeocodeV2 = async (
  address: ProvidedAddress,
  country: string,
  addressParser: AddressParser,
  geocodeExecutor: (args: GeoArgs) => GeoReturn,
  onGeocode = noop
): Promise<CustomGeocodeResult | null> => {
  const { address1, address2, suburb, postcode, state } = address

  // Note that this causes extra spacing which needs to be trimmed
  const providedAddress = `${address1 ?? ''} ${address2 ?? ''} ${suburb ?? ''} ${state ?? ''} ${postcode ?? ''}`

  // Geocode api will still return a result with component restrictions and empty address

  const componentRestrictions: google.maps.GeocoderComponentRestrictions | undefined = isUndefinedOrEmpty(providedAddress)
    ? undefined
    : {
        country,
      }

  onGeocode()
  return await geocodeByAddressV2(providedAddress, addressParser, geocodeExecutor, componentRestrictions)
}

export const geocodeByAddressV2 = async (
  address: string,
  addressParser: AddressParser,
  geocodeExecutor: (args: GeoArgs) => GeoReturn,
  componentRestrictions?: google.maps.GeocoderComponentRestrictions
): Promise<CustomGeocodeResult | null> => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const results = await geocodeExecutor({
      address,
      componentRestrictions,
    })

    if (results === null) {
      throw new Error('no results')
    }

    const [firstResult] = results
    const { valid, data } = addressParser(firstResult)

    if (!valid) {
      throw new Error('Did not pass address parser validation')
    }

    return {
      formData: data,
      formattedAddress: firstResult.formatted_address,
    }
  } catch (error: any) {
    // Assume error status was returned
    // https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingStatusCodes
    throw new Error(error)
  }
}

// Todo: Fix return type
export const generateMandatoryFields = (parsedData: InputAddressData, optionalFields: string[]): { [k: string]: string } => {
  const initialMandatoryFields: MandatoryFields = {
    address1: parsedData.address1,
    suburb: parsedData.suburb,
    state: parsedData.state,
    postcode: parsedData.postcode,
    countryCode: parsedData.countryCode,
  }

  // Remove the optional fields from initial mandatory fields if needed
  return Object.fromEntries(Object.entries(initialMandatoryFields).filter(([fieldName]) => !optionalFields.includes(fieldName)))
}

type TransformedData = StringifyWithOptionalKeys<CountryAddressComponents>

export const countryRequiredComponentsValid = (transformedData: TransformedData, country: string): boolean => {
  // Short circuit if there isn't a country configuration
  if (typeof REQUIRED_ADDRESS_COMPONENTS[country] === 'undefined') {
    return true
  }

  const { validationMatchType, components } = REQUIRED_ADDRESS_COMPONENTS[country]

  // Depending on the match type
  // For eg, we might want unitNumber AND street number to exist.
  // Or, just one is good enough
  return components[validationMatchType]((addressComponent) => !isUndefinedOrEmpty(transformedData[addressComponent]))
}

export const parseAddress =
  ({
    allowedAddressType = DEFAULT_ALLOWED_ADDRESS_TYPES,
    country,
    useLocalityLongName = false,
    isRegions = false,
    validateRule = InputAddressValidationRule.ByAddressType,
    optionalFields = [],
  }: ParseAddressConfig): AddressParser =>
  (data: google.maps.GeocoderResult): InputAddressAddressData => {
    const countryConfig = CONFIG[country] ?? CONFIG.US
    const { address_components: addressComponents } = data
    const lookupTable: {
      [x: string]: string
    } = {}

    const transformedData = {} as TransformedData

    addressComponents.forEach((value) => {
      value.types.forEach((type) => {
        const useLongName = type === 'locality' && useLocalityLongName
        lookupTable[type] = useLongName ? value.long_name : value.short_name
      })
    })

    const countryConfigKeys = Object.keys(countryConfig) as Array<keyof CountryAddressComponents>

    countryConfigKeys.forEach((property) => {
      transformedData[property] = (countryConfig[property] ?? []).reduce((accumulator: string, currentValue: string) => {
        return lookupTable[currentValue] ?? accumulator
      }, '')
    })

    const unitNumber = typeof transformedData.unitNumber !== 'undefined' && transformedData.unitNumber.length > 0 ? `${transformedData.unitNumber}/` : ''
    const address1 = `${unitNumber}${transformedData.streetNumber} ${transformedData.route}`.trim()
    const parsedData: InputAddressData = {
      address1,
      address2: '',
      suburb: transformedData.suburb,
      state: transformedData.state ?? '',
      postcode: transformedData.postcode,
      countryCode: transformedData.countryCode,
      area2: transformedData.area2 ?? '',
    }

    const mandatoryFields = generateMandatoryFields(parsedData, optionalFields)

    // Rule: ByAddressType
    //   Reference: https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
    //   address type must match allowedAddressType
    //   Note: Does not filter PO Box - it is considered a street_address

    // Rule: ByAddress
    //   Mandatory fields must be not blank
    const validation: Record<InputAddressValidationRule, () => boolean> = {
      [InputAddressValidationRule.ByAddressType]: () => allowedAddressType.some((allowType) => data.types.includes(allowType)),
      [InputAddressValidationRule.ByAddress]: () =>
        Object.keys(mandatoryFields).every((key) => mandatoryFields[key as keyof MandatoryFields].trim().length > 0),
    }

    const valid =
      (transformedData.countryCode === country &&
        validation[validateRule]() &&
        countryRequiredComponentsValid(transformedData, country) &&
        addressHasNoPO(parsedData)) ||
      isRegions

    return {
      valid,
      data: parsedData,
    }
  }

export const containsLetters = (testString: string): boolean => {
  return /[a-z]/i.test(testString)
}

export const postcodeKind = (postcodeFormat: string): 'text' | 'tel' => {
  return containsLetters(postcodeFormat) ? 'text' : 'tel'
}

// A complete GB postcode is the outward code plus the inward sector+unit. Google's
// Place Details sometimes returns only the outward code (postal_code_prefix, e.g. "M24"),
// which is incomplete and non-deliverable.
const GB_POSTCODE_PATTERN = '[A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}'
const GB_FULL_POSTCODE_REGEX = new RegExp(`^${GB_POSTCODE_PATTERN}$`)
const GB_POSTCODE_AT_END_REGEX = new RegExp(`${GB_POSTCODE_PATTERN}$`)

export const isCompleteGbPostcode = (postcode: string | undefined): boolean => {
  return GB_FULL_POSTCODE_REGEX.test((postcode ?? '').trim())
}

// The full postcode the customer saw in the autocomplete dropdown is carried in the
// selected prediction's description (e.g. "113 Windermere Road, Manchester M24 4LJ, UK"),
// even when Place Details only returns the outward code. Recover it from there so the
// customer's selection is filled with a complete postcode; return null if it isn't present.
export const recoverGbPostcodeFromDescription = (description?: string): string | null => {
  if (typeof description === 'undefined' || description.trim().length === 0) {
    return null
  }
  const parts = description.split(',').map((part) => part.trim())
  for (let i = parts.length - 1; i >= 0; i--) {
    const match = parts[i].match(GB_POSTCODE_AT_END_REGEX)
    if (match !== null) {
      return match[0].toUpperCase()
    }
  }
  return null
}

export const scrollDropDownMenu = (element: HTMLElement): void => {
  element?.scrollIntoView({ behavior: 'smooth', block: 'end' })
}

interface GetErrorMessageArgs extends CommonUIError {
  customValidationString?: string
  isError: boolean
}

export const getErrorMessage = ({ customValidationString = '', errorText, isError }: GetErrorMessageArgs): ReactNode => {
  const isErrorMessage: boolean = typeof errorText !== 'undefined' && (typeof errorText === 'string' ? errorText.length > 0 : true) && isError
  const isCustomErrorMessage = typeof customValidationString !== 'undefined' && customValidationString.length > 0

  if (isCustomErrorMessage) {
    return customValidationString
  }
  if (isErrorMessage && !isCallable(errorText)) {
    return errorText
  }
  if (isError && isCallable(errorText)) {
    return errorText()
  }
  return undefined
}
